<template>
  <Teleport to="body">
    <!-- Banner principal: sin conexión -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="isOffline"
        class="fixed top-0 inset-x-0 z-[9999] bg-error text-on-error safe-top"
      >
        <div class="px-4 py-2.5">
          <div class="flex items-center justify-center gap-2 text-label-large">
            <span class="material-symbols-rounded" style="font-size: 18px;">cloud_off</span>
            Sin conexión a internet
          </div>
          <p v-if="pendingCount > 0" class="text-center text-body-small mt-0.5 opacity-90">
            {{ pendingCount }} pedido{{ pendingCount !== 1 ? 's' : '' }} guardado{{ pendingCount !== 1 ? 's' : '' }} — se enviarán al volver la conexión
          </p>
        </div>
      </div>
    </Transition>

    <!-- Banner de sincronización: procesando cola al volver online -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="!isOffline && isSyncing"
        class="fixed top-0 inset-x-0 z-[9999] bg-amber-500 text-amber-950 px-4 py-2.5 safe-top"
      >
        <div class="flex items-center justify-center gap-2 text-label-large">
          <div class="w-4 h-4 border-2 border-amber-950 border-t-transparent rounded-full animate-spin"></div>
          Sincronizando {{ pendingCount }} pedido{{ pendingCount !== 1 ? 's' : '' }} pendiente{{ pendingCount !== 1 ? 's' : '' }}...
        </div>
      </div>
    </Transition>

    <!-- Banner de reconexión exitosa -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="showReconnected"
        class="fixed top-0 inset-x-0 z-[9999] bg-green-600 text-white px-4 py-2.5 safe-top"
      >
        <div class="flex items-center justify-center gap-2 text-label-large">
          <span class="material-symbols-rounded" style="font-size: 18px;">cloud_done</span>
          Conexión restaurada{{ syncedMessage }}
        </div>
      </div>
    </Transition>

    <!-- Error persistente de sincronización -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-full opacity-0"
    >
      <div
        v-if="lastSyncError && !isOffline && !isSyncing"
        class="fixed top-0 inset-x-0 z-[9999] bg-error text-on-error px-4 py-2.5 safe-top"
      >
        <div class="flex items-center justify-center gap-2 text-label-large">
          <span class="material-symbols-rounded" style="font-size: 18px;">error</span>
          {{ lastSyncError }}
        </div>
        <div class="flex justify-center mt-1">
          <button
            @click="retryNow"
            class="px-3 py-1 rounded-lg bg-white/20 text-body-small font-medium hover:bg-white/30 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
const { isOnline, pendingCount, isSyncing, lastSyncError, retryNow } = useOfflineQueue()
const isOffline = computed(() => !isOnline.value)

// Mostrar "Conexión restaurada" brevemente al volver online
const showReconnected = ref(false)
const syncedMessage = ref('')
let wasOffline = false
let reconnectTimer: ReturnType<typeof setTimeout> | null = null

watch(isOnline, (online) => {
  if (!online) {
    wasOffline = true
    showReconnected.value = false
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  } else if (wasOffline) {
    wasOffline = false
    syncedMessage.value = pendingCount.value > 0
      ? ` — enviando ${pendingCount.value} pedido${pendingCount.value !== 1 ? 's' : ''}...`
      : ''
    showReconnected.value = true
    reconnectTimer = setTimeout(() => {
      showReconnected.value = false
    }, 3000)
  }
})

onUnmounted(() => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
})
</script>
